"use client";

import React, {
  useCallback,
  useState,
  forwardRef,
  useEffect,
  useMemo,
  useRef,
} from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { ChevronDown, Search } from "lucide-react";
import { IconCheck } from "@tabler/icons-react";
import { Typography } from "@/components/Typography";
import { useNationalities } from "@/hooks/useNationalities";
import { type Nationality } from "@/types/nationality";
import { resolveNationality } from "@/utils/nationality";

import { CircleFlag } from "react-circle-flags";

interface CountryDropdownProps {
  options?: Nationality[];
  onChange?: (country: Nationality) => void;
  value?: string;
  selectedId?: number | string;
  disabled?: boolean;
  placeholder?: string;
  slim?: boolean;
  variant?: "default" | "dialCode";
  className?: string;
}

const formatIsd = (isd?: string | number) => {
  if (!isd) return "";
  const str = String(isd).trim();
  return str.startsWith("+") ? str : `+${str}`;
};

/**
 * Render a searchable country dropdown.
 * @param props
 * @param ref
 * @returns
 */
const CountryDropdownComponent = (
  {
    options,
    onChange,
    value,
    selectedId,
    disabled = false,
    placeholder = "Select a country",
    slim = false,
    variant = "default",
    className,
    ...props
  }: CountryDropdownProps,
  ref: React.ForwardedRef<HTMLButtonElement>,
) => {
  const { data: apiNationalities } = useNationalities();
  const nationalities = useMemo(
    () => options ?? apiNationalities ?? [],
    [options, apiNationalities],
  );

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const listContainerRef = useRef<HTMLDivElement>(null);

  const selectedCountry = useMemo(() => {
    const fromCodeOrId = resolveNationality(nationalities, {
      code: value,
      id: selectedId,
    });
    if (fromCodeOrId) return fromCodeOrId;

    if (value) {
      const cleanVal = value.replace("+", "").trim();
      const byIsd = nationalities.find(
        (n) => n.isd?.replace("+", "").trim() === cleanVal,
      );
      if (byIsd) return byIsd;
    }

    if (variant === "dialCode") {
      return nationalities.find((n) => n.code === "KH");
    }

    return undefined;
  }, [nationalities, selectedId, value, variant]);

  // Focus search input when popover opens
  useEffect(() => {
    if (open) {
      const t = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    } else {
      setQuery("");
      setActiveIndex(-1);
    }
  }, [open]);

  const handleSelect = useCallback(
    (country: Nationality) => {
      onChange?.(country);
      setOpen(false);
    },
    [onChange],
  );

  const filteredOptions = useMemo(() => {
    if (!query.trim()) return nationalities;
    const q = query.toLowerCase().replace(/^\+/, "");
    return nationalities.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.code.toLowerCase().includes(q) ||
        c.isd?.replace("+", "").includes(q),
    );
  }, [nationalities, query]);

  const displayList = useMemo(() => {
    return query
      ? filteredOptions
      : filteredOptions.filter((opt) => opt.id !== selectedCountry?.id);
  }, [query, filteredOptions, selectedCountry]);

  // Reset active index when query changes
  useEffect(() => {
    setActiveIndex(-1);
  }, [query]);

  // Scroll active item into view when navigating with arrow keys
  useEffect(() => {
    if (activeIndex >= 0 && listContainerRef.current) {
      const activeEl = listContainerRef.current.children[activeIndex] as
        HTMLElement | undefined;
      activeEl?.scrollIntoView({ block: "nearest" });
    }
  }, [activeIndex]);

  const activeIndexRef = useRef(activeIndex);
  activeIndexRef.current = activeIndex;

  const displayListRef = useRef(displayList);
  displayListRef.current = displayList;

  const handleSelectRef = useRef(handleSelect);
  handleSelectRef.current = handleSelect;

  // Declarative [key, action] tuples for smooth keyboard navigation
  const keyActionTuples = useMemo<readonly (readonly [string, () => void])[]>(
    () => [
      [
        "ArrowDown",
        () => {
          setActiveIndex((prev) =>
            prev < displayListRef.current.length - 1 ? prev + 1 : 0,
          );
        },
      ],
      [
        "ArrowUp",
        () => {
          setActiveIndex((prev) =>
            prev > 0 ? prev - 1 : displayListRef.current.length - 1,
          );
        },
      ],
      [
        "Enter",
        () => {
          const list = displayListRef.current;
          const idx = activeIndexRef.current;
          if (idx >= 0 && idx < list.length) {
            handleSelectRef.current(list[idx]);
          } else if (list.length > 0) {
            handleSelectRef.current(list[0]);
          }
        },
      ],
      [
        "Escape",
        () => {
          setOpen(false);
        },
      ],
    ],
    [],
  );

  const keyActionMap = useMemo(
    () => new Map(keyActionTuples),
    [keyActionTuples],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const action = keyActionMap.get(e.key);
      if (action) {
        e.preventDefault();
        action();
      }
    },
    [keyActionMap],
  );

  const isDialCode = variant === "dialCode";

  // Apply interactive border styles only when the trigger is enabled.
  const triggerClasses = cn(
    isDialCode
      ? "flex h-full items-center justify-between gap-1.5 rounded-l-xl bg-transparent px-3 text-base text-left shadow-none transition-colors hover:bg-subtle/60 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
      : "flex h-12 w-full items-center justify-between gap-2 whitespace-nowrap rounded-xl border bg-white px-4 text-base text-left shadow-none transition-colors hover:border-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-primary data-[state=open]:border-primary disabled:cursor-not-allowed disabled:opacity-50",
    !isDialCode &&
    "aria-invalid:border-error-foreground aria-invalid:ring-1 aria-invalid:ring-error-foreground",
    slim && "h-9 w-20 rounded-md px-3 text-sm",
    className,
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        ref={ref}
        className={triggerClasses}
        disabled={disabled}
        {...props}
      >
        {isDialCode ? (
          <div className="flex items-center gap-1.5 select-none">
            <CircleFlag
              countryCode={
                selectedCountry ? selectedCountry.code.toLowerCase() : "kh"
              }
              height="20"
              width="20"
              className="shrink-0"
            />
            <span className="text-[16px] font-semibold text-body">
              {formatIsd(selectedCountry?.isd) || "+855"}
            </span>
            <ChevronDown
              className={cn(
                "h-3.5 w-3.5 shrink-0 text-caption transition-transform duration-200",
                open && "rotate-180",
              )}
            />
          </div>
        ) : selectedCountry ? (
          <div className="flex flex-grow items-center gap-2.5 w-0 overflow-hidden">
            {!slim && (
              <CircleFlag
                countryCode={selectedCountry.code.toLowerCase()}
                height="20"
                width="20"
                className="shrink-0"
              />
            )}
            <span className="overflow-hidden text-ellipsis whitespace-nowrap text-body font-semibold">
              {slim
                ? selectedCountry.code
                : `${selectedCountry.name} (${selectedCountry.code})`}
            </span>
          </div>
        ) : (
          <span className="text-caption font-normal">{placeholder}</span>
        )}
        {!isDialCode && (
          <ChevronDown
            className={cn(
              "h-4 w-4 shrink-0 transition-transform duration-200",
              selectedCountry ? "text-body" : "text-caption",
              open && "rotate-180",
            )}
          />
        )}
      </PopoverTrigger>

      <PopoverContent
        sideOffset={4}
        collisionPadding={16}
        side="bottom"
        align="start"
        onOpenAutoFocus={(e) => e.preventDefault()}
        className={cn(
          "w-[calc(100vw-2rem)] max-w-[calc(100vw-2rem)] overflow-hidden rounded-2xl border bg-white p-0 shadow-2xl md:w-[400px] md:min-w-[400px]",
        )}
      >
        <div className="flex flex-col">
          {/* Search bar */}
          <div className="p-2">
            <div className="flex h-[46px] items-center gap-2.5 rounded-xl border bg-white px-3 py-2 transition-colors focus-within:border-primary">
              <Search size={18} className="shrink-0 text-caption" />
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Search country"
                className="flex-1  bg-transparent text-sm text-body placeholder:text-caption outline-none"
              />
            </div>
          </div>

          {/* Sticky / fixed selected item under search with border-t and border-b */}
          {!query && selectedCountry && (
            <div className="border-t border-b border-subtle bg-white px-2 py-1.5 space-y-1">
              <div className="px-1 pt-0.5">
                <Typography
                  variant="body2"
                  className="font-semibold text-caption"
                >
                  Selected
                </Typography>
              </div>
              <button
                type="button"
                onClick={() => handleSelect(selectedCountry)}
                className="flex h-11 w-full items-center justify-between gap-2.5 rounded-xl bg-subtle px-3 text-left transition-colors"
              >
                <div className="flex min-w-0 items-center gap-2.5">
                  <CircleFlag
                    countryCode={selectedCountry.code.toLowerCase()}
                    height="20"
                    width="20"
                    className="shrink-0"
                  />
                  <span className="truncate text-base font-semibold text-body">
                    {selectedCountry.name}
                  </span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-sm font-medium text-caption">
                    {formatIsd(selectedCountry.isd)}
                  </span>
                  <IconCheck
                    stroke={2}
                    className="mr-1 h-5 w-5 shrink-0 text-body"
                  />
                </div>
              </button>
            </div>
          )}

          <div
            ref={listContainerRef}
            className="tovtrip-scrollbar max-h-[260px] overflow-y-auto overscroll-contain p-2 space-y-1"
          >
            {/* List */}
            {displayList.length === 0 ? (
              <div className="px-3 py-4 text-center text-sm text-caption">
                No country found.
              </div>
            ) : (
              displayList.map((option, index) => {
                const isSelected = option.id === selectedCountry?.id;
                const isHighlighted = index === activeIndex;

                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => handleSelect(option)}
                    onMouseEnter={() => setActiveIndex(index)}
                    className={cn(
                      "flex h-11 w-full items-center justify-between gap-2.5 rounded-xl px-3 text-left transition-colors hover:bg-subtle",
                      (isSelected || isHighlighted) && "bg-subtle",
                    )}
                  >
                    <div className="flex min-w-0 items-center gap-2.5">
                      <CircleFlag
                        countryCode={option.code.toLowerCase()}
                        height="20"
                        width="20"
                        className="shrink-0"
                      />
                      <span
                        className={cn(
                          "truncate text-base text-body",
                          isSelected ? "font-semibold" : "font-normal",
                        )}
                      >
                        {option.name}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-sm font-medium text-caption">
                        {formatIsd(option.isd)}
                      </span>
                      {isSelected && (
                        <IconCheck
                          stroke={2}
                          className="mr-1 h-5 w-5 shrink-0 text-body"
                        />
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

CountryDropdownComponent.displayName = "CountryDropdownComponent";

export const CountryDropdown = forwardRef(CountryDropdownComponent);
